/**
 * @fileoverview Reusable department-logo upload field (thumbnail + upload/replace/remove).
 * @module system-web/components/DepartmentLogoUploadField
 *
 * Responsibilities: - Render a single department-logo slot: a thumbnail of the current
 *                     logo (or an empty placeholder) plus Upload/Replace/Remove controls.
 *                   - Own the logo-scoped FileUploadModal and translate a successful
 *                     upload into a canonical BrandingLogoRef the caller persists.
 *                   - Resolve a server-signed SAS URL for the thumbnail because the stored
 *                     `blobUrl` is the raw private Azure blob URL (not directly renderable).
 * Dependencies:     - FileUploadModal (image-only logo guardrails).
 *                   - @firesquirrel/core-domain (BrandingLogoRefLike shape).
 *                   - systemUploadedFileService (getUploadedFileDownloadUrl) for the
 *                     inherently-online signed URL needed to render a private blob.
 * Architecture:     - Presentation + upload-trigger only. It performs NO data writes:
 *                     it raises `onChange(ref | null)` and the parent persists the patch
 *                     local-first (Dexie-first → sync). Shared by the department detail
 *                     slideout and the onboarding wizard so both write the identical
 *                     canonical shape (DRY). Blob rendering is an inherently-online read
 *                     exception: it uses a service helper to fetch a short-lived SAS URL.
 * Notes:            - File upload + blob rendering are the allowed inherently-online ops.
 *                     The component never calls an API endpoint directly; it calls the
 *                     local-first service helper for the signed URL.
 */

import { useEffect, useState } from 'react';
import type { BrandingLogoRefLike } from '@firesquirrel/core-domain';
import type { UploadedFileLocal } from '@firesquirrel/local-db';
import { tryGetFileUrlResolver } from '@firesquirrel/file-service-client/client';
import FileUploadModal, {
  LOGO_ALLOWED_TYPES,
  LOGO_MAX_SIZE_BYTES,
} from './FileUploadModal';

/**
 * @process uploadedFileToLogoRef
 * Steps:           Project the uploaded-file record onto the canonical logo ref shape.
 * Inputs:          UploadedFileLocal returned by the upload endpoint.
 * Outputs:         BrandingLogoRefLike (uploadedFileId + blobUrl + display metadata).
 * Failure Handling:none — pure projection.
 * Idempotency:     REQUIRED (pure).
 */
export function uploadedFileToLogoRef(file: UploadedFileLocal): BrandingLogoRefLike {
  return {
    uploadedFileId: file._id,
    blobUrl: file.blobUrl,
    fileName: file.fileName,
    mimeType: file.mimeType,
  };
}

/**
 * @contract DepartmentLogoUploadFieldProps
 * Purpose: Inputs for a single logo slot editor.
 * Fields:  label — slot caption; value — current logo ref (undefined when empty);
 *          disabled — read-only/saving guard; onChange — receives the new ref or null.
 * Rules:   onChange(null) clears the slot; the parent owns persistence.
 * Sync:    local-first (persistence happens in the parent).
 */
export interface DepartmentLogoUploadFieldProps {
  label: string;
  value?: BrandingLogoRefLike;
  disabled?: boolean;
  onChange: (ref: BrandingLogoRefLike | null) => void;
}

/**
 * @component DepartmentLogoUploadField
 * @reusable
 * Usage:       Department branding logo slot (detail slideout + onboarding wizard).
 * Rules:       Presentation + upload-trigger only — no direct data writes; raises onChange.
 * Purpose:     Show a logo thumbnail and let the admin upload/replace/remove it.
 * Props:       DepartmentLogoUploadFieldProps.
 * Behavior:    Opens a logo-scoped FileUploadModal; on success maps the file to a ref.
 *              When the ref has an uploadedFileId, fetches a short-lived SAS download URL
 *              from the server and uses that for the thumbnail; falls back to the stored
 *              blobUrl if resolution is unavailable.
 * Data Source: none (parent persists); upload + signed URL resolution are inherently online.
 * Constraints: image-only (PNG/JPG/SVG/WebP), 2MB rail enforced by the modal.
 * Performance: trivial; one signed-URL fetch per mounted logo ref.
 */
export default function DepartmentLogoUploadField({
  label,
  value,
  disabled = false,
  onChange,
}: DepartmentLogoUploadFieldProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);

  const renderUrl = resolvedUrl || value?.blobUrl;
  const hasLogo = Boolean(value?.uploadedFileId || value?.blobUrl);

  useEffect(() => {
    if (!value?.uploadedFileId) {
      setResolvedUrl(null);
      return;
    }

    const fileId = value.uploadedFileId;
    let cancelled = false;
    void (async () => {
      const resolver = tryGetFileUrlResolver();
      if (!resolver) return;
      try {
        const url = await resolver.resolve(fileId);
        if (!cancelled) {
          setResolvedUrl(url);
        }
      } catch (err) {
        console.error('[DepartmentLogoUploadField] Failed to resolve logo URL:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [value?.uploadedFileId]);

  return (
    <div className="flex items-center gap-4">
      <div className="w-16 h-16 rounded-md bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-xs overflow-hidden shrink-0">
        {renderUrl ? (
          <img
            src={renderUrl}
            alt={`${label} preview`}
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          'Logo'
        )}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-sm text-gray-600">{label}</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={disabled}
            onClick={() => setModalOpen(true)}
            className="px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {hasLogo ? 'Replace' : 'Upload'}
          </button>
          {hasLogo && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange(null)}
              className="px-3 py-1.5 rounded-md text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Remove
            </button>
          )}
        </div>
      </div>

      <FileUploadModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={(file) => onChange(uploadedFileToLogoRef(file))}
        allowedTypes={LOGO_ALLOWED_TYPES}
        maxSizeBytes={LOGO_MAX_SIZE_BYTES}
        category="letterhead"
        title={`Upload ${label}`}
        subtitle="PNG, JPG, SVG, or WebP — used in report headers"
        acceptHint="PNG, JPG, SVG, WebP (max 2MB)"
        showImagePreview
      />
    </div>
  );
}
